import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import GameArena from "@/pages/GameArena";
import GameHub from "@/pages/GameHub";
import Limbo from "@/pages/Limbo";
import Tower from "@/pages/Tower";
import Membership from "@/pages/Membership";
import Wallet from "@/pages/Wallet";
import Leaderboard from "@/pages/Leaderboard";
import Admin from "@/pages/Admin";
import AuthCallback from "@/pages/AuthCallback";
import Profile from "@/pages/Profile";
import Crews from "@/pages/Crews";
import CrewDetail from "@/pages/CrewDetail";
import Plaza from "@/pages/Plaza";
import Matches from "@/pages/Matches";
import Shop from "@/pages/Shop";
import Ecosystem from "@/pages/Ecosystem";
import Entertainment from "@/pages/Entertainment";
import Register from "@/pages/Register";
import Maintenance from "@/pages/Maintenance";
import Nav from "@/components/Nav";
import React from "react";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen grid place-items-center p-6 text-center" data-testid="error-boundary">
          <div>
            <div className="font-display font-black text-3xl mb-2">Something glitched in the grid.</div>
            <button className="btn-neon mt-4" onClick={() => { this.setState({ hasError: false }); window.location.href = "/"; }}>← BACK TO ECOSYSTEM</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-white/70 font-mono">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <Nav />
      <Routes>
        <Route path="/" element={<Ecosystem />} />
        <Route path="/welcome" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/games" element={<Protected><GameHub /></Protected>} />
        <Route path="/game" element={<Protected><GameArena /></Protected>} />
        <Route path="/limbo" element={<Protected><Limbo /></Protected>} />
        <Route path="/tower" element={<Protected><Tower /></Protected>} />
        <Route path="/plaza" element={<Plaza />} />
        <Route path="/matches" element={<Protected><Matches /></Protected>} />
        <Route path="/shop" element={<Protected><Shop /></Protected>} />
        <Route path="/ecosystem" element={<Protected><Ecosystem /></Protected>} />
        <Route path="/register" element={<Register />} />
        <Route path="/business" element={<Maintenance section="Business" />} />
        <Route path="/entertainment" element={<Protected><Entertainment /></Protected>} />
        <Route path="/shopping" element={<Maintenance section="Shopping" />} />
        <Route path="/sports" element={<Maintenance section="Sports" />} />
        <Route path="/wallet" element={<Protected><Wallet /></Protected>} />
        <Route path="/profile" element={<Protected><Profile /></Protected>} />
        <Route path="/crews" element={<Crews />} />
        <Route path="/crews/:id" element={<CrewDetail />} />
        <Route path="/membership" element={<Membership />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ErrorBoundary>
      <BrowserRouter>
        <Toaster
          theme="dark"
          position="top-right"
          toastOptions={{
            style: {
              background: "rgba(18, 48, 36, 0.95)",
              border: "1px solid rgba(46, 227, 138, 0.35)",
              color: "#fff",
              borderRadius: "16px",
              fontFamily: "Plus Jakarta Sans, sans-serif",
              backdropFilter: "blur(12px)",
            },
          }}
        />
        <AppRouter />
      </BrowserRouter>
      </ErrorBoundary>
    </AuthProvider>
  );
}
